import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { SearchableCustomerSelect } from './SearchableCustomerSelect';
import { SearchableTransactionSelect } from './SearchableTransactionSelect';
import { PayableSelect } from './PayableSelect';
import { renderPaymentMethodOptions } from '../utils/paymentMethodOptions';
import { dollarsToCents, centsToAmountString, formatCents, validatePaymentAgainstBalance } from '../utils/money';

// Stable, unique id per mounted instance so two forms on one page (e.g. the list
// form and the floating form) don't collide on radio/label ids.
let instanceCounter = 0;

/**
 * PaymentForm - Shared form for tendering a payment against a transaction or a
 * lease (RTO) agreement. Used by the Payments list and the floating quick-payment
 * window so the field names, money handling, and validation live in ONE place.
 *
 * Money is handled as integer cents internally (see utils/money) and submitted as
 * an exact decimal string. RTO agreements are passed in already normalized (see
 * utils/apiNormalizers) — camelCase fields with `*Cents` money.
 *
 * @param {Object} props
 * @param {Function} props.apiCall - authenticated fetch wrapper
 * @param {Array} props.transactions - raw transaction rows
 * @param {Array} props.payments - raw payment rows (for transaction balance calc)
 * @param {Array} props.vehicles - raw vehicle rows (for transaction display)
 * @param {Array} props.rtoAgreements - NORMALIZED RTO agreements (normalizeRtoAgreements)
 * @param {Function} props.onSubmit - async submit handler; receives the payload and
 *   should throw on failure so the error surfaces in the form
 * @param {Function} props.onCancel - optional cancel handler
 * @param {Object} props.initialData - initial form data (edit / prefilled mode)
 * @param {string} props.submitLabel - submit button label (default: "Tender Payment")
 */
export function PaymentForm({
  apiCall,
  transactions = [],
  payments = [],
  vehicles = [],
  rtoAgreements = [],
  onSubmit,
  onCancel,
  initialData = {},
  submitLabel = 'Tender Payment',
  remoteCandidates = false,
}) {
  const instanceId = useMemo(() => `payment-form-${++instanceCounter}`, []);
  const [paymentType, setPaymentType] = useState(initialData.rto_agreement_id ? 'rto' : 'transaction');
  const [selectedRto, setSelectedRto] = useState(null);
  // Remaining balance of the selected transaction, in integer cents (null = none).
  const [transactionRemainingCents, setTransactionRemainingCents] = useState(null);
  const [formError, setFormError] = useState(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    transaction_id: '',
    rto_agreement_id: '',
    amount: '',
    payment_method: 'cash_app',
    payment_date: new Date().toISOString().split('T')[0],
    ...initialData,
  });

  // Update form data if initialData changes (for edit / prefilled mode)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData((prev) => ({ ...prev, ...initialData }));
      if (initialData.transaction_id) {
        setPaymentType('transaction');
      } else if (initialData.rto_agreement_id) {
        setPaymentType('rto');
      }
    }
  }, [initialData]);

  const resetSelections = (overrides = {}) => {
    setFormData((prev) => ({ ...prev, transaction_id: '', rto_agreement_id: '', amount: '', ...overrides }));
    setSelectedRto(null);
    setTransactionRemainingCents(null);
  };

  const handleSelectRto = (rtoId) => {
    const rto = rtoAgreements.find((r) => r.id === parseInt(rtoId, 10)) || null;
    setSelectedRto(rto);
    // Default the amount to what's currently owed (past due), falling back to the
    // recurring installment. Selecting a lease also fills in its customer.
    const prefillCents = rto ? (rto.totalOwedCents > 0 ? rto.totalOwedCents : rto.recurringAmountCents) : null;
    setFormData((prev) => ({
      ...prev,
      rto_agreement_id: rtoId,
      customer_id: rto ? rto.customerId : prev.customer_id,
      amount: rto ? centsToAmountString(prefillCents) : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.customer_id) {
      setFormError('Please select a customer');
      return;
    }
    if (paymentType === 'transaction' && !formData.transaction_id) {
      setFormError('Please select a transaction');
      return;
    }
    if (paymentType === 'rto' && !formData.rto_agreement_id) {
      setFormError('Please select a lease agreement');
      return;
    }

    // Single source of truth for the "exceeds balance" rule (exact integer cents).
    const amountCents = dollarsToCents(formData.amount);
    const { maxCents, label } =
      paymentType === 'rto'
        ? { maxCents: selectedRto ? selectedRto.balanceRemainingCents : null, label: 'RTO remaining balance' }
        : { maxCents: transactionRemainingCents, label: 'remaining balance' };

    const validationError = validatePaymentAgainstBalance(amountCents, maxCents, label);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    try {
      const paymentData = {
        customer_id: parseInt(formData.customer_id, 10),
        // Submit money as an exact decimal string, never a float.
        amount: centsToAmountString(amountCents),
        payment_method: formData.payment_method,
        payment_date: formData.payment_date,
        ...(paymentType === 'transaction' ? { transaction_id: formData.transaction_id } : {}),
        ...(paymentType === 'rto' ? { rto_agreement_id: formData.rto_agreement_id } : {}),
      };
      await onSubmit(paymentData);
    } catch (error) {
      setFormError(error.message || 'An error occurred');
    }
  };

  const visibleRtoAgreements = rtoAgreements.filter(
    (rto) => !formData.customer_id || rto.customerId === parseInt(formData.customer_id, 10)
  );

  return (
    <Form onSubmit={handleSubmit}>
      {formError && <Alert variant="danger">{formError}</Alert>}

      {/* Payment Type Toggle */}
      <Form.Group className="mb-3">
        <Form.Label>Payment For</Form.Label>
        <div>
          <Form.Check
            inline
            type="radio"
            id={`${instanceId}-type-transaction`}
            label="Transaction (Rental/Fee)"
            name={`${instanceId}-paymentType`}
            checked={paymentType === 'transaction'}
            onChange={() => {
              setPaymentType('transaction');
              resetSelections();
            }}
          />
          <Form.Check
            inline
            type="radio"
            id={`${instanceId}-type-rto`}
            label="Lease Agreement (Long Term Lease)"
            name={`${instanceId}-paymentType`}
            checked={paymentType === 'rto'}
            onChange={() => {
              setPaymentType('rto');
              resetSelections();
            }}
          />
        </div>
      </Form.Group>

      <Row>
        <Col md={6}>
          <SearchableCustomerSelect
            apiCall={apiCall}
            selectedCustomerId={formData.customer_id}
            onSelectCustomer={(customerId) => resetSelections({ customer_id: customerId })}
            required={true}
          />
        </Col>
        <Col md={6}>
          {remoteCandidates ? (
            <PayableSelect apiCall={apiCall} customerId={formData.customer_id} kind={paymentType}
              selectedId={paymentType === 'rto' ? formData.rto_agreement_id : formData.transaction_id}
              onSelect={item => {
                const remainingCents = item ? dollarsToCents(item.remaining_balance) : null;
                if (paymentType === 'rto') {
                  const owedCents = item ? dollarsToCents(item.total_owed) : 0;
                  const recurringCents = item ? dollarsToCents(item.recurring_amount) : 0;
                  setSelectedRto(item ? { balanceRemainingCents: remainingCents, totalOwedCents: owedCents, recurringAmountCents: recurringCents } : null);
                  setFormData(prev => ({ ...prev, rto_agreement_id: item?.id || '', amount: item ? centsToAmountString(Math.min(remainingCents, owedCents > 0 ? owedCents : recurringCents)) : '' }));
                } else {
                  setTransactionRemainingCents(remainingCents);
                  setFormData(prev => ({ ...prev, transaction_id: item?.id || '', amount: item ? centsToAmountString(remainingCents) : '' }));
                }
              }} />
          ) : paymentType === 'transaction' ? (
            <SearchableTransactionSelect
              transactions={transactions.filter((t) => t.status !== 'cancelled')}
              payments={payments}
              vehicles={vehicles}
              customerId={formData.customer_id}
              selectedTransactionId={formData.transaction_id}
              onSelectTransaction={(transactionId, remainingBalance) => {
                const remainingCents = dollarsToCents(remainingBalance);
                setFormData((prev) => ({
                  ...prev,
                  transaction_id: transactionId,
                  amount: transactionId ? centsToAmountString(remainingCents) : '',
                }));
                setTransactionRemainingCents(transactionId ? remainingCents : null);
              }}
              required={true}
            />
          ) : (
            <Form.Group className="mb-3" controlId={`${instanceId}-rto`}>
              <Form.Label>Lease Agreement *</Form.Label>
              <Form.Select
                value={formData.rto_agreement_id}
                onChange={(e) => handleSelectRto(e.target.value)}
                required
              >
                <option value="">Select a Lease Agreement</option>
                {visibleRtoAgreements.map((rto) => (
                  <option key={rto.id} value={rto.id}>
                    #{rto.id} - {rto.customerName} - {rto.vehicleYear} {rto.vehicleMake} {rto.vehicleModel} (Owes: {formatCents(rto.totalOwedCents)})
                  </option>
                ))}
              </Form.Select>
              {selectedRto && (
                <Form.Text className="text-muted">
                  Balance: {formatCents(selectedRto.balanceRemainingCents)} remaining
                  {selectedRto.totalOwedCents > 0 && (
                    <span className="text-danger ms-2">(Past due: {formatCents(selectedRto.totalOwedCents)})</span>
                  )}
                </Form.Text>
              )}
            </Form.Group>
          )}
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId={`${instanceId}-amount`}>
            <Form.Label>Amount *</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              required
            />
            {paymentType === 'transaction' && transactionRemainingCents > 0 && (
              <Form.Text className="text-muted">Remaining balance: {formatCents(transactionRemainingCents)}</Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId={`${instanceId}-method`}>
            <Form.Label>Payment Method</Form.Label>
            <Form.Select
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
            >
              {renderPaymentMethodOptions()}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3" controlId={`${instanceId}-date`}>
        <Form.Label>Payment Date</Form.Label>
        <Form.Control
          type="date"
          value={formData.payment_date}
          onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
          required
        />
      </Form.Group>

      <div className="d-flex gap-2">
        <Button type="submit" variant="primary">
          {submitLabel}
        </Button>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </Form>
  );
}

export default PaymentForm;
